import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import MetricCard from './components/MetricCard';
import ProgressChart from './components/ProgressChart';
import SkillRadarChart from './components/SkillRadarChart';
import GoalsTracker from './components/GoalsTracker';
// Pilot disabled: sidebar calendar fetches My Plans data; retained for post-pilot restore.
// import SidebarCalendar from './components/SidebarCalendar';
import ProgressJourney from './components/ProgressJourney';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

import * as apiSvc from '../../utils/api';

const ProfileProgress = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const transformRawInterviews = (rawList, startIndex = 0) => {
    return (Array.isArray(rawList) ? rawList : []).map((interview, index) => {
      const examDetails = interview.exam_details || {};
      const result = interview.result || {};

      return {
        id: startIndex + index + 1,
        session_id: interview.session_id,
        is_challenge: interview.is_challenge,
        results_released: interview.results_released,
        company: examDetails.company || '',
        role: examDetails.role || examDetails.interview_type || 'Interview',
        type: examDetails.type || examDetails.interview_type || '',
        duration: examDetails.duration || result.overallData?.duration || 0,
        date: new Date(interview.created_at).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        createdAt: interview.created_at,
        overallScore: result.overallData?.score || result.overall_score || result.OverallData?.Score || 0,
        computedMetrics: result.computedMetrics || result.ComputedMetrics || null,
        scores: (result.parameters || result.Parameters || []).map(p => {
          const name = p.name || p.Name || p.label || 'Skill';
          const value = p.score !== undefined ? p.score : (p.Score !== undefined ? p.Score : (p.value !== undefined ? p.value : 0));
          return { name, current: value };
        }),
        strengthsCount: (result.strengths || []).length,
        weaknessesCount: (result.weaknesses || []).length,
        integrityScore: result.integrityScore || 0,
      };
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [profileData, interviewsData, statsData] = await Promise.all([
        apiSvc.getCurrentUser().catch(err => { console.warn('Profile fetch failed:', err); return null; }),
        apiSvc.getInterviews(10, 0).catch(err => { console.warn('Interviews fetch failed:', err); return { interviews: [] }; }),
        apiSvc.getInterviewStats().catch(err => { console.warn('Stats fetch failed:', err); return null; }),
      ]);
      
      if (!profileData && !interviewsData && !statsData) {
        setError('Unable to load progress data offline. Please connect once to sync.');
        setLoading(false);
        return;
      }

      setUser(profileData || { full_name: 'Explorer' });

      const rawInterviews = interviewsData?.interviews || interviewsData || [];
      const transformedInterviews = transformRawInterviews(rawInterviews, 0);

      setInterviews(transformedInterviews);
      setStats(statsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data');
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    try {
      setLoadingMore(true);
      const nextOffset = interviews.length;
      const interviewsData = await apiSvc.getInterviews(10, nextOffset);
      const rawInterviews = interviewsData?.interviews || interviewsData || [];
      const transformed = transformRawInterviews(rawInterviews, nextOffset);
      setInterviews(prev => [...prev, ...transformed]);
    } catch (err) {
      console.error('Failed to load more interviews:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // ─── Compute all derived data from REAL interviews ───

  // Skill averages (for radar chart) — computed from actual parameter scores
  const skillMetrics = {};
  interviews.forEach(interview => {
    (interview.scores || []).forEach(skill => {
      if (!skillMetrics[skill.name]) skillMetrics[skill.name] = { total: 0, count: 0 };
      skillMetrics[skill.name].total += skill.current;
      skillMetrics[skill.name].count += 1;
    });
  });

  const skillsData = Object.keys(skillMetrics).length > 0
    ? Object.keys(skillMetrics).map(name => ({
      name,
      current: Math.round(skillMetrics[name].total / skillMetrics[name].count),
      target: 85
    }))
    : [];

  // ─── Compute REAL metric deltas ───
  const latest = interviews[0];
  const previous = interviews[1];

  // Score delta (latest vs previous)
  const scoreDelta = (latest && previous)
    ? (latest.overallScore - previous.overallScore) : null;

  // Average score delta vs running average
  const avgScore = stats?.avg_score || 0;

  // Total practice hours from global backend statistics
  const totalDurationMins = stats?.total_duration || 0;
  const totalHours = Math.round(totalDurationMins / 60);

  // Hours delta (last interview vs global average duration)
  const globalTotalInterviews = stats?.total_interviews || 0;
  const avgDuration = globalTotalInterviews > 0
    ? Math.round(totalDurationMins / globalTotalInterviews) : 0;
  const latestDurationDelta = latest
    ? Math.round(latest.duration - avgDuration) : null;

  // Filler word trend
  const latestFillers = latest?.computedMetrics?.fillerWordCount;
  const prevFillers = previous?.computedMetrics?.fillerWordCount;
  const fillerDelta = (latestFillers !== undefined && prevFillers !== undefined)
    ? prevFillers - latestFillers : null; // positive = improvement

  // ─── Goals: computed from REAL data ───
  const totalInterviews = stats?.total_interviews || interviews.length || 0;
  const goalsData = [
    {
      title: "Complete 10 mock interviews",
      progress: Math.min((totalInterviews / 10) * 100, 100),
      completed: totalInterviews >= 10
    },
    {
      title: "Achieve 80+ average score",
      progress: avgScore > 0 ? Math.min((avgScore / 80) * 100, 100) : 0,
      completed: avgScore >= 80
    },
    {
      title: "Reduce fillers below 5",
      progress: latestFillers !== undefined
        ? (latestFillers <= 5 ? 100 : Math.min(((20 - latestFillers) / 15) * 100, 99))
        : 0,
      completed: latestFillers !== undefined && latestFillers <= 5
    },
    {
      title: `${stats?.current_streak || 0}+ day practice streak`,
      progress: Math.min(((stats?.current_streak || 0) / 7) * 100, 100),
      completed: (stats?.current_streak || 0) >= 7
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavigationHeader />
        <main className="pt-24 pb-12 px-6 lg:px-12 max-w-[1440px] mx-auto">
            <SkeletonLoader variant="stat" count={4} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8"><SkeletonLoader variant="chart" count={1} /></div>
                <div className="lg:col-span-4"><SkeletonLoader variant="chart" count={1} /></div>
            </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-3xl border border-slate-100">
          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Oops!</h2>
          <p className="text-slate-500 mb-8">{error}</p>
          <button onClick={fetchData} className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans relative overflow-hidden">
      {/* Premium Header Background */}
      <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
      <NavigationHeader />
      
      <main className="pt-24 pb-20 px-6 lg:px-12 relative z-10">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* LEFT COLUMN: Main Performance Data */}
            <div className="lg:col-span-8 space-y-8">

              {/* Metric Cards — all real data, no hardcoding */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  label="Readiness"
                  value={`${Math.round(avgScore)}%`}
                  delta={scoreDelta}
                  deltaLabel=" pts"
                />
                <MetricCard
                  label="Practice"
                  value={`${totalHours}h`}
                  delta={latestDurationDelta}
                  deltaLabel="m"
                />
                <MetricCard
                  label="Sessions"
                  value={totalInterviews.toString()}
                  delta={null}
                />
                <MetricCard
                  label="Streak"
                  value={`${stats?.current_streak || 0}d`}
                  delta={null}
                  color="slate"
                />
              </div>

              {/* ═══════ THE NEW PROGRESS JOURNEY ═══════ */}
              <ProgressJourney 
                interviews={interviews} 
                rank={user?.rank} 
                hasMore={interviews.length < (stats?.total_interviews || 0)}
                loadingMore={loadingMore}
                loadMore={loadMore}
                totalInterviews={stats?.total_interviews}
              />
            </div>

            {/* RIGHT COLUMN: Sidebar Insights & Goals */}
            <div className="lg:col-span-4 space-y-8">
              
              {/* Start Interview CTA */}
              <div className="bg-slate-900 rounded-2xl p-8 text-white relative overflow-hidden group">
                <Icon name="Zap" size={80} className="absolute -right-4 -bottom-4 text-white/5 group-hover:text-white/10 transition-all duration-500" />
                <h4 className="text-lg font-bold mb-2 relative z-10">Start Preparation</h4>
                <p className="text-slate-400 text-xs mb-6 relative z-10 leading-relaxed">Ready to measure your progress? Start a new mock interview session now.</p>
                <button 
                  onClick={() => navigate('/interview-setup')}
                  className="w-full py-4 bg-emerald-500 text-white rounded-xl text-[12px] font-black tracking-widest hover:bg-emerald-600 transition-all duration-300 relative z-10 flex items-center justify-center gap-2"
                >
                  <Icon name="Play" size={14} className="fill-current" /> Start Mock Interview
                </button>
              </div>

              {/* Skills Radar */}
              {skillsData.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 p-8">
                  <h3 className="text-[10px] font-black tracking-[0.2em] text-slate-900 mb-6 flex items-center gap-2">
                    <Icon name="Search" size={12} className="text-emerald-500" /> Skills Radar
                  </h3>
                  <SkillRadarChart skills={skillsData} />
                </div>
              )}

              {/* Goals — all computed from real data */}
              <GoalsTracker
                goals={goalsData}
                totalInterviews={totalInterviews}
                avgScore={avgScore}
              />

              {/* Calendar */}
              {/* Pilot disabled: sidebar calendar uses My Plans during the full product.
              <SidebarCalendar />
              */}
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileProgress;
